import {
  Unit,
  any,
  Fun,
  IOWidget,
  Widget,
  promise,
  wait,
  Action,
  Options
} from "../main";
import { option, nothing } from "../widget";

type Cache<a> =
  | { readonly kind: "empty" }
  /** Cache has a `value` which was stored in the given UNIX time. */
  | { readonly kind: "populated"; value: a; loadedAt: number };

interface CacheOptions {
  readonly expiryInSeconds: number;
  readonly forceFlush?: boolean;
}

/** The result the promise should return
 *
 * All errors should be properly converted to a value
 * @typeparam a The type of the successful result value
 */
export type HttpResult<a> = (
  | { kind: "result"; value: a }
  | { kind: "unauthorized" }
  | { kind: "failed" }) & { status: number };

/**
 * This is an asyncstate which has successfully loaded with a value of type a
 */
export type LoadedAsyncState<a> = { kind: "loaded"; value: a };

/**
 * This is an asyncstate which represents a loading phase.
 * If we are reloading, value holds the old cached value
 */
export type LoadingAsyncState<a> = { kind: "loading"; value?: a };

/**
 * It's the state type of a state machine underpinning the process
 * behind calling a JS promise and managing its retry cycle and general errors
 * The state transitions to:
 * a) unloaded => unloaded
 * b) loading => loaded | any of the errors
 * c) connection-error => loading
 * d) failed => failed
 * e) unauthorized => unauthorized
 * 
 * When the state machine is running, it contains a lazy promise [[p]], a cache
 * and optional a retry count
 */
export type AsyncState<a> = (
  | LoadedAsyncState<a>
  | { kind: "unloaded" }
  | ((
      | LoadingAsyncState<a>
      | { kind: "connection-error", }
      | { kind: "unauthorized"; status: number }
      | { kind: "failed"; status: number }) & {
      p: Fun<Unit, Promise<HttpResult<a>>>;
    })) & { cache: Cache<a> } &  { retryCount?: number };

type RetryOptions = {
  interval?: number
  maxAttempts?: number
}

const defaultMaxAttempts = 10
const defaultInterval = 10

export interface AsyncOptions<a> extends Options {
  /** loading is a widget invoked to draw during a loading state
   * it could make use of the old value from the loading async state
   */
  readonly loading?: IOWidget<LoadingAsyncState<a>, never>;
  /**
   * Is a widget invoked when we have a connection error
   * We do not change the state here because we retry automatically
   */
  readonly connectionError?: Widget<never>;
  /**
   * Is a widget invoked when we get an unauthorized error
   * The widget determines what happens in this case via this widget
   */
  readonly unauthorizedError?: Widget<Action<AsyncState<a>>>;
  /**
   * It is a widget to manage all data failures and also to change the state
   */
  readonly otherError?: Widget<Action<AsyncState<a>>>;
  /**
   * A widget invoked when the state machine enters the unloaded state
   * It may change the state
   */
  readonly unloaded?: Widget<Action<AsyncState<a>>>;
  /**
   * These are the cache and retry options
   */
  readonly cache?: CacheOptions;
  readonly retry?: RetryOptions
}

/**
 * An asynchronous wrapper around the w widget.
 * Async is responsible for the dynamics, that is the execution of the 
 * asynchronous process emboded by the supplied async state.
 * @param w The widget used to render&manage the loaded value
 * If you don't provide this is same as providing the empty widget `nothing()`
 * @param s0 The inner async state. It usually starts as unloaded.
 * Every time the inner asyncstate is set to loading by the client application, the asynchronous
 * state machine is started and the promise will fire. This means than in case of a promise
 * containing a remote call to an API, the networking communication runs at that point.
 *
 *@param options governing the rendering of temporary asynchronous states, caching, and retrying
 *
 */
export const async = <a>(
  w?: IOWidget<a, Action<AsyncState<a>>>,
  options?: AsyncOptions<a>
) => (s0: AsyncState<a>): Widget<Action<AsyncState<a>>> =>
  any<Action<AsyncState<a>>>(options)(
    s0.kind == "loaded"
      ? w
        ? [w(s0.value)]
        : []
      : s0.kind == "loading"
      ? [
          ...(options && options.loading
            ? [options.loading(s0).never<Action<AsyncState<a>>>()]
            : []),
          promise<Unit, Action<AsyncState<a>>>(
            _ => {
              const cacheHit =
                options &&
                options.cache &&
                !options.cache.forceFlush &&
                s0.cache.kind == "populated" &&
                (Date.now() - s0.cache.loadedAt.valueOf()) / 1000 <=
                  options.cache.expiryInSeconds;

              const p =
                cacheHit && s0.cache.kind == "populated"
                  ? Promise.resolve<HttpResult<a>>({
                      kind: "result",
                      value: s0.cache.value,
                      status: 200
                    })
                  : s0.p({});

              return p.then<Action<AsyncState<a>>>(res => _ =>
                res.kind == "result"
                  ? {
                      ...s0,
                      kind: "loaded",
                      retryCount: 0,
                      value: res.value,
                      cache:
                        options && options.cache
                          ? cacheHit
                            ? s0.cache
                            : {
                                kind: "populated",
                                value: res.value,
                                loadedAt: Date.now()
                              }
                          : { kind: "empty" }
                    }
                  : res.kind == "unauthorized"
                  ? { ...s0, kind: "unauthorized", status: res.status }
                  : { ...s0, kind: "failed", status: res.status }
              );
            },
            {
              on_fail: (e: any): Action<AsyncState<a>> => s1 => {
                console.log("The request failed for the following reason:")
                console.log(e)
                // NOTE: only connection-error is fed inside here, unless somebody intercepts it
                return  s1.kind == "loading" ||
                  s1.kind == "failed" ||
                  s1.kind == "connection-error" ||
                  s1.kind == "unauthorized"
                    ? s0.retryCount == undefined || s0.retryCount < (options && options.retry && options.retry.maxAttempts || defaultMaxAttempts)
                    ? { ...s1, kind: "connection-error", retryCount: s1.retryCount ? s1.retryCount + 1 : 1 }
                    : { ...s1, kind: 'failed', retryCount: 0, status: 500 }
                    : s1
                  },
              key: `async-promise-${option("key", options) || ""}`
            }
          )({})
        ]
      : s0.kind == "connection-error"
      ? [
          ...(options && options.connectionError != undefined
            ? [options.connectionError.never<Action<AsyncState<a>>>()]
            : []),
          s0.retryCount == undefined || s0.retryCount < (options && options.retry && options.retry.maxAttempts || defaultMaxAttempts)
          ? wait<Action<AsyncState<a>>>(
            options && options.retry && options.retry.interval || defaultInterval, 
            { key: `async-connection-error-${option("key", options) || ""}` }
          )(() => _ => ({ ...s0, kind: "loading" }))
          : nothing()
        ]
      : s0.kind == "unauthorized"
      ? options && options.unauthorizedError
        ? [options.unauthorizedError]
        : []
      : s0.kind == "failed"
      ? options && options.otherError
        ? [options.otherError]
        : []
      : options && options.unloaded
      ? [options.unloaded]
      : []
  );

/**
 * A way to create a (initial) unloaded async state.
 * @param cache to start with
 */
export const unloadedAsyncState = <a>(cache?: Cache<a>): AsyncState<a> => ({
  kind: "unloaded",
  cache: cache ? { ...cache } : { kind: "empty" }
});

/**
 *
 * @deprecated Not much used explicitly. Just a helper function.
 */
export const unauthorizedAsyncState = <a>(status: number): AsyncState<a> => ({
  kind: "unauthorized",
  cache: { kind: "empty" },
  status: status,
  p: () => new Promise(_ => {})
});

/**
 * @deprecated Not much used explicitly. Just a helper function.
 */
export const errorAsyncState = <a>(status: number): AsyncState<a> => ({
  kind: "failed",
  cache: { kind: "empty" },
  status: status,
  p: () => new Promise(_ => {})
});


/**
 * A way to create an already-resolved async state
 * 
 * @param v The resolved value injected
 */
export const loadedAsyncState = <a>(v: a): AsyncState<a> => ({
  kind: "loaded",
  cache: {kind: "populated", value: v, loadedAt: Date.now() },
  value: v
});

/**
 * A way to setup an asynchronous process, around an inputted lazy promise.
 * @param p The promise
 * @param old_value The previously loaded value (if available or relevant, used by the loading? widget)
 * @param cache 
 */
export const loadingAsyncState = <a>(
  p: Fun<Unit, Promise<HttpResult<a>>>,
  old_value?: a,
  cache?: Cache<a>
): AsyncState<a> => ({
  kind: "loading",
  value: old_value,
  cache: cache ? { ...cache } : { kind: "empty" },
  p: p
});
